import { useEffect, useState } from "react";
import PayrollCompanyLayout from "../../../components/layouts/PayrollCompanyLayout";
import { Helmet } from "react-helmet-async";
import { TbTriangleFilled } from "react-icons/tb";
import CompanyDetails from "./views/CompanyDetails";
import CompanyLevels from "./views/company_levels";
import CostCenters from "./views/cost_centers";
import CompanyGrouping from "./views/company_grouping";
import GradeLevels from "./views/grade_levels";
import { COMPANY_GROUPING_TYPE } from "../../../models/company-grouping.dto";
import { useSearchParams } from "react-router-dom";
import DisciplinaryTypes from "./views/disciplinary_types";
import GrievanceTypes from "./views/grievance_types";
import EmployeeBands from "./views/employee_bands";
import WorkDaysInAPeriod from "./views/work_days_in_a_period";
import AssetTypes from "./views/asset_types";
import JobTitles from "./views/job_titles";
import PayPeriods from "./views/pay_periods";
import CompanyCurrency from "./views/company_currency";
import CompanyApprover from "./views/CompanyApprover";
import Unions from "./views/unions";
import ErpIntegrationMappings from "./views/erp_integration_mapping";
import Departments from "./views/departments";
import CompanyTaxCodes from "./views/tax_codes";
import {
	PERMISSIONS,
	useCheckUserPermissions,
} from "../../../helpers/usePermissionManager";
import DashboardPermissionChecker from "../../../components/layouts/resources/DashboardPermissionChecker";
import TaxLevelMappings from "./views/TaxLevelMappings";
import CompanyTaxRules from "./views/tax_rules";
import CompanyPaymentInstitutions from "./views/company_payment_institutions";
import CompanyPaymentInstitutionBranches from "./views/company_payment_institution_branches";
import PaymentSetup from "./views/payment_setup";

type NavItem = {
	key: string;
	name: string;
	component: any;
	type?: string;
};

const navItems: NavItem[] = [
	{
		key: "companyDetails",
		name: "Company Details",
		component: CompanyDetails,
	},
	{
		key: "companyLevels",
		name: "Company Levels",
		component: CompanyLevels,
	},
	{
		key: "companyApprover",
		name: "Company Approvers",
		component: CompanyApprover,
	},
	{
		key: "companyCurrency",
		name: "Company Currencies",
		component: CompanyCurrency,
	},
	{
		key: "taxLevelMappings",
		name: "Tax Level Mappings",
		component: TaxLevelMappings,
	},
	{
		key: "companyPaymentInstittutions",
		name: "Payment Institutions",
		component: CompanyPaymentInstitutions,
	},
	{
		key: "companyPaymentInstittutionBranches",
		name: "Payment Institution Branches",
		component: CompanyPaymentInstitutionBranches,
	},
	{
		key: "paymentSetup",
		name: "Payment Setup",
		component: PaymentSetup,
	},
	{
		key: "taxCodes",
		name: "Tax Codes",
		component: CompanyTaxCodes,
	},
	{
		key: "taxRules",
		name: "Tax Rules",
		component: CompanyTaxRules,
	},
	{
		key: "costCenters",
		name: "Cost Centers",
		component: CostCenters,
	},
	{
		key: "regions",
		name: "Regions",
		component: CompanyGrouping,
		type: COMPANY_GROUPING_TYPE.REGION,
	},
	{
		key: "divisions",
		name: "Divisions",
		component: CompanyGrouping,
		type: COMPANY_GROUPING_TYPE.DIVISION,
	},
	{
		key: "stations",
		name: "Stations",
		component: CompanyGrouping,
		type: COMPANY_GROUPING_TYPE.STATION,
	},
	{
		key: "tribes",
		name: "Tribes",
		component: CompanyGrouping,
		type: COMPANY_GROUPING_TYPE.TRIBE,
	},
	{
		key: "departments",
		name: "Departments",
		component: Departments,
	},
	{
		key: "gradeLevels",
		name: "Grade Levels",
		component: GradeLevels,
	},
	{
		key: "bands",
		name: "Employee Bands & Notches",
		component: EmployeeBands,
	},
	{
		key: "workDaysInAPeriod",
		name: "Work Days In A Period",
		component: WorkDaysInAPeriod,
	},
	{
		key: "payPeriods",
		name: "Pay Periods",
		component: PayPeriods,
	},
	{
		key: "jobTitles",
		name: "Job Titles",
		component: JobTitles,
	},
	{
		key: "erpIntegrationMappings",
		name: "ERP Integration Mappings",
		component: ErpIntegrationMappings,
	},

	{
		key: "disciplinaryTypes",
		name: "Disciplinary Types",
		component: DisciplinaryTypes,
	},
	{
		key: "grievanceTypes",
		name: "Grievance Types",
		component: GrievanceTypes,
	},
	{
		key: "assetTypes",
		name: "Assets Type",
		component: AssetTypes,
	},
	{
		key: "unions",
		name: "Unions",
		component: Unions,
	},
];

const CompanySettings = () => {
	const { checkUserPermissionsOr } = useCheckUserPermissions();
	const [searchParams, setSearchParams] = useSearchParams();
	const view = searchParams.get("view");
	const [selectedOption, setSelectedOption] = useState<NavItem>();

	useEffect(() => {
		if (selectedOption) return;

		if (!view) {
			setSearchParams({ view: "companyDetails" });
			setSelectedOption(navItems[0]);
			return;
		}

		const selectedInfo = navItems.find((item) => item.key === view);
		setSelectedOption(selectedInfo);
	}, [view]);

	const handleNavItemClick = (item: NavItem) => {
		setSearchParams({ view: item.key });
		setSelectedOption(item);
	};

	return (
		<PayrollCompanyLayout>
			<Helmet>{selectedOption?.name} | Company Settings</Helmet>

			<h1 className="w-full font-bold text-xl bg-white mb-8">
				Company Settings{" "}
				<span className="text-[15px] font-medium text-[#007aff]">
					({selectedOption?.name})
				</span>
			</h1>
			<div
				className="grow w-full flex gap-4"
				style={{ height: "calc(100% - 60px)" }}
			>
				<section className="h-full w-[20%] text-2sm rounded-lg flex flex-col gap-y-6 bg-white border py-7 px-4 overflow-y-auto children-scroll-bar">
					{navItems.map((item) => (
						<>
							{(item.key === "companyDetails" ||
								checkUserPermissionsOr([
									PERMISSIONS.COMPANIES_READ,
									PERMISSIONS.COMPANIES_WRITE,
								])) && (
								<div
									key={item.key}
									className={`flex items-center gap-3 cursor-pointer ${
										selectedOption?.key === item.key
											? "text-primary-blue"
											: "text-black"
									}`}
									onClick={() => handleNavItemClick(item)}
								>
									<TbTriangleFilled
										className={`rotate-90 shrink-0 ${
											selectedOption?.key !== item.key && "text-white"
										}`}
									/>
									<span>{item.name}</span>
								</div>
							)}
						</>
					))}
				</section>
				<section className="h-full w-[80%] rounded-lg bg-white border py-8 px-7 overflow-y-auto children-scroll-bar">
					{selectedOption?.key === "companyDetails" && (
						<selectedOption.component />
					)}
					{selectedOption && selectedOption?.key !== "companyDetails" && (
						<DashboardPermissionChecker
							permissions={[
								PERMISSIONS.COMPANIES_READ,
								PERMISSIONS.COMPANIES_WRITE,
							]}
							permissionRule="OR"
						>
							<selectedOption.component type={selectedOption.type} />
						</DashboardPermissionChecker>
					)}
				</section>
			</div>
		</PayrollCompanyLayout>
	);
};

export default CompanySettings;
